import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapView } from '../components/MapView';
import { StationDispatch } from '../components/StationDispatch';
import {
  Banner, Card, Empty, IncidentBadge, PriorityPill, SlaBadge, UnitBadge,
} from '../components/ui';
import { useFlash, useNow, useRealtime, useResource } from '../state/hooks';
import { api } from '../lib/api';
import { clock, elapsed, titleCase } from '../lib/format';
import type { Incident, Unit, UnitStatus } from '../lib/types';
import { useAuth } from '../state/AuthContext';

/**
 * The dispatcher's console.
 *
 * Everything here answers one question: what is waiting, and who can go. The
 * queue is sorted by priority then age — never by arrival alone — so a P1
 * raised thirty seconds ago sits above a P4 that has been open an hour, which
 * is the whole point of banding incidents in the first place.
 */

const OPEN_STATUSES = 'draft,pending,dispatched,en_route,on_scene';

export function DispatchConsole() {
  const { can } = useAuth();
  const now = useNow();
  const [flash, showFlash] = useFlash();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  const [busy, setBusy] = useState(false);

  const incidents = useResource<{ incidents: Incident[] }>(`/incidents?status=${OPEN_STATUSES}&limit=200`);
  const units = useResource<{ units: Unit[] }>('/units');
  // Readable by every role, unlike /admin/config which needs system.config.
  const clientConfig = useResource<{
    map: { center: { latitude: number; longitude: number }; zoom: number; tileUrl: string | null; attribution: string | null };
  }>('/config');

  const refresh = useCallback(() => {
    incidents.reload();
    units.reload();
  }, [incidents, units]);

  useRealtime(['incidents', 'units'], refresh, refresh);

  const list = useMemo(() => {
    const all = incidents.data?.incidents ?? [];
    const filtered = showUnassignedOnly ? all.filter((incident) => incident.units.length === 0) : all;
    return [...filtered].sort((a, b) => {
      const priority = (a.priority_level ?? 99) - (b.priority_level ?? 99);
      if (priority !== 0) return priority;
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
  }, [incidents.data, showUnassignedOnly]);

  const selected = list.find((incident) => incident.id === selectedId)
    ?? incidents.data?.incidents.find((incident) => incident.id === selectedId)
    ?? null;

  // Keep a selection alive across refreshes, but drop it once the incident closes.
  useEffect(() => {
    if (selectedId && !incidents.data?.incidents.some((incident) => incident.id === selectedId)) {
      setSelectedId(null);
    }
  }, [incidents.data, selectedId]);

  const center = clientConfig.data?.map.center ?? { latitude: 33.6844, longitude: 73.0479 };

  const act = async (label: string, action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
      showFlash('ok', label);
      refresh();
    } catch (err) {
      showFlash('error', (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const unassignedCount = (incidents.data?.incidents ?? []).filter((i) => i.units.length === 0).length;

  return (
    <div className="stack">
      {flash && <Banner kind={flash.kind}>{flash.message}</Banner>}

      <div className="columns side">
        <Card
          title={`Queue (${list.length})`}
          flush
          actions={
            <button
              className={`chip${showUnassignedOnly ? ' on' : ''}`}
              onClick={() => setShowUnassignedOnly((v) => !v)}
            >
              Unassigned {unassignedCount}
            </button>
          }
        >
          {incidents.loading && !incidents.data ? (
            <Empty>Loading queue…</Empty>
          ) : list.length === 0 ? (
            <Empty>Nothing open. The queue is clear.</Empty>
          ) : (
            <ul className="list scroll" style={{ maxHeight: 'calc(100vh - 220px)' }}>
              {list.map((incident) => (
                <li key={incident.id}>
                  <button
                    className={`item${incident.id === selectedId ? ' selected' : ''}`}
                    onClick={() => setSelectedId(incident.id)}
                  >
                    <span className="strip" style={{ background: incident.priority_color ?? 'var(--border)' }} />
                    <span className="grow">
                      <span className="item-title">
                        <PriorityPill
                          code={incident.priority_code}
                          color={incident.priority_color}
                          level={incident.priority_level}
                        />
                        <span className="mono">{incident.incident_no}</span>
                        <SlaBadge
                          dueAt={incident.sla_due_at}
                          breachedAt={incident.sla_breached_at}
                          dispatchedAt={incident.dispatched_at}
                          now={now}
                        />
                      </span>
                      <span className="item-sub truncate">
                        {incident.category_name ?? 'Unclassified'} · {incident.location_address ?? 'No address'}
                      </span>
                      <span className="item-sub">
                        {incident.units.length === 0
                          ? 'No units assigned'
                          : incident.units.map((unit) => unit.callSign).join(', ')}
                      </span>
                    </span>
                    <span className="stack tight" style={{ alignItems: 'flex-end' }}>
                      <IncidentBadge status={incident.status} />
                      <span className="faint">{elapsed(incident.created_at, now)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="stack">
          <Card title="Situation" flush>
            <MapView
              units={units.data?.units ?? []}
              incidents={incidents.data?.incidents ?? []}
              center={center}
              selectedId={selectedId}
              onSelect={(kind, id) => kind === 'incident' && setSelectedId(id)}
              height={380}
              tileUrl={clientConfig.data?.map.tileUrl}
              attribution={clientConfig.data?.map.attribution}
            />
          </Card>

          {selected ? (
            <DispatchPanel
              incident={selected}
              units={units.data?.units ?? []}
              busy={busy}
              canAssign={can('dispatch.assign')}
              canClose={can('incidents.close')}
              onAct={act}
              now={now}
            />
          ) : (
            <Card title="Dispatch">
              <Empty>Select an incident from the queue to assign units.</Empty>
            </Card>
          )}

          <UnitBoard units={units.data?.units ?? []} now={now} canAssign={can('dispatch.assign')} onAct={act} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- dispatch panel

function DispatchPanel({
  incident,
  units,
  busy,
  canAssign,
  canClose,
  onAct,
  now,
}: {
  incident: Incident;
  units: Unit[];
  busy: boolean;
  canAssign: boolean;
  canClose: boolean;
  onAct: (label: string, action: () => Promise<unknown>) => Promise<void>;
  now: number;
}) {
  const [selectedUnits, setSelectedUnits] = useState<string[]>([]);
  const [note, setNote] = useState('');

  useEffect(() => {
    setSelectedUnits([]);
    setNote('');
  }, [incident.id]);

  const assignedIds = new Set(incident.units.map((unit) => unit.unitId));

  /**
   * Available units, nearest first when the incident has coordinates. Distance
   * is advisory — the dispatcher knows about the closed road and the system
   * does not — so ordering is the only thing it drives.
   */
  const candidates = useMemo(() => {
    const available = units.filter(
      (unit) => !assignedIds.has(unit.id) && !['off_duty', 'out_of_service'].includes(unit.status),
    );
    if (incident.latitude == null || incident.longitude == null) return available;
    const distance = (unit: Unit) => {
      if (unit.latitude == null || unit.longitude == null) return Number.POSITIVE_INFINITY;
      const toRad = (value: number) => (value * Math.PI) / 180;
      const dLat = toRad(unit.latitude - incident.latitude!);
      const dLon = toRad(unit.longitude - incident.longitude!);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(incident.latitude!)) * Math.cos(toRad(unit.latitude)) * Math.sin(dLon / 2) ** 2;
      return 2 * 6371 * Math.asin(Math.sqrt(a));
    };
    return available
      .map((unit) => ({ unit, km: distance(unit) }))
      .sort((a, b) => {
        // Free units first, then by distance.
        const freeDelta = Number(b.unit.status === 'available') - Number(a.unit.status === 'available');
        return freeDelta !== 0 ? freeDelta : a.km - b.km;
      })
      .map(({ unit, km }) => Object.assign(unit, { distanceKm: Number.isFinite(km) ? km : null }));
  }, [units, incident, assignedIds]);

  return (
    <Card
      title={
        <div className="row">
          <PriorityPill code={incident.priority_code} color={incident.priority_color} level={incident.priority_level} />
          <Link to={`/incidents/${incident.id}`} className="mono">{incident.incident_no}</Link>
          <IncidentBadge status={incident.status} />
          <SlaBadge
            dueAt={incident.sla_due_at}
            breachedAt={incident.sla_breached_at}
            dispatchedAt={incident.dispatched_at}
            now={now}
          />
        </div>
      }
      actions={<span className="faint">opened {elapsed(incident.created_at, now)} ago</span>}
    >
      <div className="stack">
        <div>
          <div><strong>{incident.category_name ?? 'Unclassified'}</strong> — {incident.location_address ?? 'No address given'}</div>
          {incident.location_details && <div className="faint">{incident.location_details}</div>}
          <p className="muted" style={{ whiteSpace: 'pre-wrap', margin: '6px 0 0' }}>{incident.narrative}</p>
        </div>

        <div>
          <h4>Police station</h4>
          <StationDispatch incident={incident} canDispatch={canAssign} onChanged={() => void onAct('Station updated', async () => undefined)} />
        </div>

        {incident.units.length > 0 && (
          <div>
            <h4>Assigned</h4>
            <ul className="list">
              {incident.units.map((assigned) => (
                <li key={assigned.assignmentId}>
                  <div className="item" style={{ cursor: 'default' }}>
                    <span className="grow">
                      <span className="item-title">{assigned.callSign} — {assigned.name}</span>
                      <span className="item-sub">
                        {titleCase(assigned.state)} · since {clock(assigned.assignedAt)}
                      </span>
                    </span>
                    <UnitBadge status={assigned.unitStatus} />
                    {canAssign && (
                      <button
                        className="btn sm"
                        disabled={busy}
                        onClick={() =>
                          void onAct(`${assigned.callSign} released`, () =>
                            api.post(`/units/${assigned.unitId}/release`, { incidentId: incident.id }),
                          )
                        }
                      >
                        Release
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {canAssign && (
          <>
            <div>
              <h4>Send{incident.latitude != null ? ' — nearest first' : ''}</h4>
              {candidates.length === 0 ? (
                <p className="faint">No units are on duty and free.</p>
              ) : (
                <div className="row tight">
                  {candidates.slice(0, 12).map((unit) => {
                    const chosen = selectedUnits.includes(unit.id);
                    const distanceKm = (unit as Unit & { distanceKm?: number | null }).distanceKm;
                    return (
                      <button
                        key={unit.id}
                        className={`chip${chosen ? ' on' : ''}`}
                        title={`${unit.name} · ${unit.status.replace('_', ' ')}`}
                        onClick={() =>
                          setSelectedUnits((current) =>
                            chosen ? current.filter((id) => id !== unit.id) : [...current, unit.id],
                          )
                        }
                      >
                        {unit.call_sign}
                        {unit.status !== 'available' && ' •'}
                        {distanceKm != null && ` ${distanceKm.toFixed(1)}km`}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="row">
              <button
                className="btn primary"
                disabled={busy || selectedUnits.length === 0}
                onClick={() =>
                  void onAct(`Assigned ${selectedUnits.length} unit(s)`, async () => {
                    await api.post('/units/assign', { incidentId: incident.id, unitIds: selectedUnits });
                    setSelectedUnits([]);
                  })
                }
              >
                Assign {selectedUnits.length > 0 ? `(${selectedUnits.length})` : ''}
              </button>

              {incident.status !== 'resolved' && (
                <button
                  className="btn"
                  disabled={busy}
                  onClick={() =>
                    void onAct('Incident resolved', () =>
                      api.post(`/incidents/${incident.id}/status`, { status: 'resolved', note: note || undefined }),
                    )
                  }
                >
                  Resolve
                </button>
              )}
              {canClose && incident.status === 'resolved' && (
                <button
                  className="btn"
                  disabled={busy}
                  onClick={() => void onAct('Incident closed', () =>
                    api.post(`/incidents/${incident.id}/status`, { status: 'closed' }),
                  )}
                >
                  Close
                </button>
              )}
              {canClose && (
                <button
                  className="btn ghost"
                  disabled={busy}
                  onClick={() =>
                    void onAct('Incident cancelled', () =>
                      api.post(`/incidents/${incident.id}/status`, {
                        status: 'cancelled',
                        note: note || 'Cancelled by dispatch',
                      }),
                    )
                  }
                >
                  Cancel incident
                </button>
              )}
            </div>

            <div className="row">
              <input
                type="text"
                placeholder="Add a note to the incident log"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                style={{ flex: 1 }}
              />
              <button
                className="btn"
                disabled={busy || !note.trim()}
                onClick={() =>
                  void onAct('Note added', async () => {
                    await api.post(`/incidents/${incident.id}/notes`, { body: note.trim() });
                    setNote('');
                  })
                }
              >
                Add note
              </button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- unit board

const BOARD_STATUSES: UnitStatus[] = ['available', 'assigned', 'en_route', 'on_scene', 'busy', 'out_of_service', 'off_duty'];

function UnitBoard({
  units,
  now,
  canAssign,
  onAct,
}: {
  units: Unit[];
  now: number;
  canAssign: boolean;
  onAct: (label: string, action: () => Promise<unknown>) => Promise<void>;
}) {
  const grouped = useMemo(() => {
    const map = new Map<UnitStatus, Unit[]>();
    for (const status of BOARD_STATUSES) map.set(status, []);
    for (const unit of units) map.get(unit.status)?.push(unit);
    return map;
  }, [units]);

  return (
    <Card title={`Status board (${units.length} units)`} flush>
      <div className="scroll-md">
        <table>
          <thead>
            <tr>
              <th>Unit</th>
              <th>Status</th>
              <th>Assignment</th>
              <th>For</th>
              {canAssign && <th>Set</th>}
            </tr>
          </thead>
          <tbody>
            {BOARD_STATUSES.flatMap((status) =>
              (grouped.get(status) ?? []).map((unit) => (
                <tr key={unit.id}>
                  <td>
                    <strong>{unit.call_sign}</strong>
                    <div className="faint">{unit.name}{unit.operator_name ? ` · ${unit.operator_name}` : ''}</div>
                  </td>
                  <td><UnitBadge status={unit.status} /></td>
                  <td>
                    {unit.assignments.length === 0 ? (
                      <span className="faint">—</span>
                    ) : (
                      unit.assignments.map((assignment) => (
                        <div key={assignment.assignmentId}>
                          <Link to={`/incidents/${assignment.incidentId}`} className="mono">
                            {assignment.incidentNo}
                          </Link>
                          <span className="faint"> {assignment.address ?? ''}</span>
                        </div>
                      ))
                    )}
                  </td>
                  <td className="faint">{elapsed(unit.status_updated_at, now)}</td>
                  {canAssign && (
                    <td>
                      <select
                        value=""
                        onChange={(event) => {
                          const next = event.target.value as UnitStatus;
                          if (!next) return;
                          void onAct(`${unit.call_sign} → ${next.replace('_', ' ')}`, () =>
                            api.post(`/units/${unit.id}/status`, {
                              status: next,
                              incidentId: unit.assignments[0]?.incidentId ?? null,
                            }),
                          );
                        }}
                      >
                        <option value="">Change…</option>
                        {BOARD_STATUSES.filter((status) => status !== unit.status).map((status) => (
                          <option key={status} value={status}>{titleCase(status)}</option>
                        ))}
                      </select>
                    </td>
                  )}
                </tr>
              )),
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
